import { Box, Flex } from '@chakra-ui/react';
import { OverlayDialog, Spinner, toasterApi } from '@ui';
import React, { Component, lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router';

import { PermissionCheck } from '@/components';
import { LabelComposerProvider } from '@/features/Tracking';
import Header from '@/components/header/Header';
import { MfaAuthentication } from '@/features/Authentication';
import { actionExecution, useAppDispatch, useAppSelector } from '@/state';
import { ProjectsApi } from '@/state/api';
import { actions } from '@/state/constants/appActions';
import {
  clearSelectedProject,
  clearSelectedProjectSheet,
  clearSelectedRelease,
} from '@/state/slices';
import { ApiCodes } from '@/types';
import { getRoutePermissions } from '@/utils/permissionUtils';
import { loginPathFor } from '@/utils/safeNext';
import Health from '../pages/Health';

// Lazy load components

const DefaultErrorPage = lazy(() => import('@pages/DefaultError'));
const Login = lazy(() => import('@pages/Login'));
const Assembly = lazy(() => import('@pages/Assembly'));
const Glazing = lazy(() => import('@pages/Glazing'));
const Installation = lazy(() => import('@pages/Installation'));
const ProductionChecklists = lazy(() => import('@/pages/ProductionChecklists'));
const Packing = lazy(() => import('@pages/Packing'));
const Shipping = lazy(() => import('@pages/Shipping'));
const ProductionSummary = lazy(() => import('@/pages/ProductionSummary'));
const WorkOrders = lazy(() => import('@/pages/WorkOrders'));
const Projects = lazy(() => import('@/pages/Projects'));
const TimeSheet = lazy(() => import('@/pages/TimeSheet'));
const RfiSubmittals = lazy(() => import('@/pages/RfiSubmittals'));
const QualityControl = lazy(() => import('@/pages/QualityControl'));
const Procurement = lazy(() => import('@/pages/Procurement'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const Receiving = lazy(() => import('@/pages/Receiving'));
const SiteDelivery = lazy(() => import('@/pages/StockDispatch'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Releases = lazy(() => import('@/pages/Releases'));
const Users = lazy(() => import('@/pages/Users'));
const Profile = lazy(() => import('@/pages/Profile'));
const MasterFiles = lazy(() => import('@/pages/MasterFiles'));
const Financials = lazy(() => import('@/pages/Financials'));
const FabricationProductionSummary = lazy(
  () => import('@/pages/FabricationProductionSummary'),
);
const FabricationWorkOrders = lazy(
  () => import('@/pages/FabricationWorkOrders'),
);
const FabricationWorkers = lazy(() => import('@/pages/FabricationWorkers'));
const Tracking = lazy(() => import('@/pages/Tracking'));
const TrackingReport = lazy(() => import('@/pages/TrackingReport'));
const TrackingItem = lazy(() => import('@/pages/TrackingItem'));
const Scan = lazy(() => import('@/pages/Scan'));

// Error boundary component for React components
class ReactErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ReactErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // eslint-disable-next-line no-console
      console.error('Error details:', this.state.error);
      return (
        <Suspense fallback={<Spinner isLoading={true} text="Loading..." />}>
          <DefaultErrorPage error={this.state.error || undefined} />
        </Suspense>
      );
    }

    return this.props.children;
  }
}

// Main layout component
const MainLayout = () => {
  const location = useLocation();
  const { current } = useAppSelector((state) => state.auth);

  const hideHeaderPaths = ['/login', '/logout'];

  const apiState = useAppSelector((state) => state.apiState);
  const dispatch = useAppDispatch();

  // Track the last processed timestamp to prevent processing the same state multiple times
  const lastProcessedTimestampRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    // Only process if code and action are present
    if (!apiState.code || !apiState.action) {
      return;
    }

    // Skip if already processed this timestamp
    if (
      apiState.timestamp &&
      lastProcessedTimestampRef.current === apiState.timestamp
    ) {
      return;
    }

    // Mark timestamp as processed
    if (apiState.timestamp) {
      lastProcessedTimestampRef.current = apiState.timestamp;
    }

    // Skip if code is Success
    if (apiState.code === 'Success') {
      return;
    }

    if (apiState.actionType && apiState.message) {
      queueMicrotask(() => {
        toasterApi({
          type: apiState.actionType,
          status: apiState.status as keyof ApiCodes | undefined,
          code: apiState.code,
          message: apiState.message,
          detail: apiState.detail,
          showDetails: apiState.showDetails,
        });
      });
    }

    // Execute the action and then reset
    const executeAction = async () => {
      if (apiState.code === 'MFARequired') {
        OverlayDialog.open('mfaVerfication', {
          overlayId: 'mfaVerfication',
          content: <MfaAuthentication />,
        });
      }

      try {
        await actionExecution({
          action: apiState.action as (typeof actions)[keyof typeof actions],
          dispatch: dispatch,
          args: {
            userid: current?.userid ?? undefined,
          },
        });
      } catch (error) {
        // Error handling if needed
      } finally {
        // Reset the API state after action execution completes
        await actionExecution({
          action: actions.resetCode,
          dispatch: dispatch,
        });
      }
    };

    executeAction();
  }, [
    apiState.code,
    apiState.action,
    apiState.timestamp,
    apiState.actionType,
    apiState.message,
    apiState.status,
    apiState.detail,
    apiState.showDetails,
    dispatch,
    current?.userid,
  ]);

  return (
    <ReactErrorBoundary>
      <LabelComposerProvider>
        <Flex
          direction="column"
          minH="100vh"
          minW="100vw"
          overflow="auto"
          key={`user-${current?.userid ?? 'anonymous'}`}
        >
          {!hideHeaderPaths.includes(location.pathname) && current?.userid && (
            <Header />
          )}

          <Flex as="main" direction="column" p={5} flex={100}>
            <Outlet />
          </Flex>

          <Box as="footer" textAlign="center" pb={5}>
            ©2025
          </Box>
        </Flex>
        <OverlayDialog.Viewport />
      </LabelComposerProvider>
    </ReactErrorBoundary>
  );
};

// Layout component for protected routes
const ProtectedLayout = () => {
  const isAuth = useAppSelector(
    (state) =>
      state.auth.current?.userid !== null &&
      state.auth.current?.userid !== undefined,
  );
  const selectedProjectId = useAppSelector(
    (state) => state.project.selectedProject.id,
  );
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const {
    data: accessibleProjects,
    isLoading: isProjectsLoading,
    isSuccess: isProjectsSuccess,
    isError: isProjectsError,
  } = ProjectsApi.Api.useGetAllProjectsQuery(undefined, {
    skip: !isAuth,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const hasStaleProjectSelection =
    isProjectsSuccess &&
    Boolean(selectedProjectId) &&
    !accessibleProjects?.some((project) => project.id === selectedProjectId);
  const shouldClearProjectSelection =
    Boolean(selectedProjectId) && (isProjectsError || hasStaleProjectSelection);
  const routeNeedsProjectValidation =
    !pathname.startsWith('/users') && !pathname.startsWith('/profile');

  React.useEffect(() => {
    if (!shouldClearProjectSelection) return;
    dispatch(clearSelectedProject());
    dispatch(clearSelectedRelease());
    dispatch(clearSelectedProjectSheet());
  }, [dispatch, shouldClearProjectSelection]);

  if (!isAuth) {
    return <Navigate to={loginPathFor(pathname, search)} replace />;
  }

  if (
    routeNeedsProjectValidation &&
    selectedProjectId &&
    (isProjectsLoading ||
      (!isProjectsSuccess && !isProjectsError) ||
      shouldClearProjectSelection)
  ) {
    return <Spinner isLoading={true} text="Loading Projects..." />;
  }

  return (
    <PermissionCheck
      permissions={getRoutePermissions(pathname)}
      fallback={<Navigate to="/" replace />}
    >
      <Outlet />
    </PermissionCheck>
  );
};

// Layout component for public routes
const PublicLayout = () => {
  return <Outlet />;
};

// Root redirect component that checks auth and redirects appropriately
const RootRedirect = () => {
  const isAuth = useAppSelector(
    (state) =>
      state.auth.current?.userid !== null &&
      state.auth.current?.userid !== undefined,
  );

  if (isAuth) {
    // If authenticated, redirect to a specific protected route
    // Using qualitycontrol as the default landing page
    return <Navigate to="/masterfiles" replace />;
  }

  // If not authenticated, redirect to login
  return <Navigate to="/login" replace />;
};

// Error boundary component for router errorElement
const ErrorBoundary = () => {
  return <DefaultErrorPage />;
};

// Loading component
const LoadingFallback = () => (
  <Spinner isLoading={true} text="Loading Page..." />
);

const LoadPage = (Component: React.ComponentType) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Create the router configuration
export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/',
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: <RootRedirect />,
          },
          {
            path: 'login',
            element: LoadPage(Login),
          },
          {
            path: 'error',
            element: LoadPage(DefaultErrorPage),
          },
          {
            path: 'health',
            element: LoadPage(Health),
          },
        ],
      },
      {
        path: '/',
        element: <ProtectedLayout />,
        children: [
          {
            index: true,
            element: LoadPage(QualityControl),
          },
          {
            path: 'assembly',
            element: LoadPage(Assembly),
          },
          {
            path: 'glazing',
            element: LoadPage(Glazing),
          },
          {
            path: 'installation',
            element: LoadPage(Installation),
          },
          {
            path: 'production-checklists',
            element: LoadPage(ProductionChecklists),
          },
          {
            path: 'fabrication-summary',
            element: LoadPage(FabricationProductionSummary),
          },
          {
            path: 'fabrication-workorders',
            element: LoadPage(FabricationWorkOrders),
          },
          {
            path: 'fabrication-workers',
            element: LoadPage(FabricationWorkers),
          },
          {
            path: 'packing',
            element: LoadPage(Packing),
          },
          {
            path: 'shipping',
            element: LoadPage(Shipping),
          },
          {
            path: 'productionsummary',
            element: LoadPage(ProductionSummary),
          },
          {
            path: 'workorder',
            element: LoadPage(WorkOrders),
          },
          {
            path: 'timesheet',
            element: LoadPage(TimeSheet),
          },
          {
            path: 'rfisubmittals',
            element: LoadPage(RfiSubmittals),
          },
          {
            path: 'qualitycontrol',
            element: LoadPage(QualityControl),
          },
          {
            path: 'financials',
            element: LoadPage(Financials),
          },
          {
            path: 'procurement',
            element: LoadPage(Procurement),
          },
          {
            path: 'inventory',
            element: LoadPage(Inventory),
          },
          {
            path: 'receiving',
            element: LoadPage(Receiving),
          },
          {
            path: 'site-delivery',
            element: LoadPage(SiteDelivery),
          },
          {
            path: 'inventory-dispatch',
            element: <Navigate to="/site-delivery" replace />,
          },
          {
            path: 'analytics',
            element: LoadPage(Analytics),
          },
          {
            path: 'projects',
            element: LoadPage(Projects),
          },
          {
            path: 'releases',
            element: LoadPage(Releases),
          },
          {
            path: 'users',
            element: LoadPage(Users),
          },
          {
            path: 'profile',
            element: LoadPage(Profile),
          },
          {
            path: 'masterfiles',
            element: LoadPage(MasterFiles),
          },
          {
            path: 'tracking',
            element: LoadPage(Tracking),
          },
          {
            path: 'tracking/items/:code',
            element: LoadPage(TrackingItem),
          },
          {
            path: 't/:code',
            element: LoadPage(TrackingReport),
          },
          {
            path: 'scan',
            element: LoadPage(Scan),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);

// Legacy component for backward compatibility
const AppRoutes = () => {
  return <Outlet />;
};

export default AppRoutes;
